import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { MyMedicalPaOrdersTableData } from "../../../../data-model/tablesData";
import { TableNames } from "../../../../enums/tableNames";
import { RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { MyMedicalPaOrdersState } from "../../myPaOrders/hooks/useMyPaOrdersTableData";
import { recursivelyGeneratePayloadForInitialFetchForPostgres } from "../../myPaOrders/utils/recursivelyGeneratePayloadForInitialFetch";
import { makePostCallForInitialDataFetch } from "../apis/makePostCallForInitialDataFetch";
import { createFilterPayloadForAllOpenOrdersForPostgres } from "../utils/generateFilterPayloadForAllOpenOrders";
import { useValidFilters } from "./useValidFilters";

/**
 * Orchestrates the initial data fetch for the All Open Orders table. Generates
 * payloads for all pages up to the previously fetched batch (from table context),
 * then triggers the bulk fetch. Re-fetches when toggled via setInitialDataFetchingExecuted(false).
 *
 * @param setAllOpenOrdersState - State setter for the orders loading/data/error state
 * @param setTableData - State setter for the transformed table row data
 * @returns initialDataFetchingExecuted flag and its setter for re-fetch control
 */
export const useInitialDataFetchingForTable = (
  setAllOpenOrdersState: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersState>
  >,
  setTableData: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersTableData[]>
  >,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const [initialDataFetchingExecuted, setInitialDataFetchingExecuted] =
    useState(false);

  const orgId = getOrgIdForFetchExternalWorklist();

  const { getTableDataFromContext } = useTablesContext();

  const tableAttributes = getTableDataFromContext(TableNames.ALL_OPEN_ORDERS);

  const validFilters = useValidFilters();

  useEffect(() => {
    if (
      !initialDataFetchingExecuted &&
      authStatusOptions &&
      authStatusOptions.length > 0
    ) {
      const payload = createFilterPayloadForAllOpenOrdersForPostgres(
        validFilters,
        tableAttributes?.searchedText ?? "",
        orgId,
        tableAttributes?.sortByField,
        tableAttributes?.sortAscending,
      );
      const payloads = recursivelyGeneratePayloadForInitialFetchForPostgres(
        payload,
        tableAttributes?.lastBatchFetched ?? 0,
        tableAttributes?.sortByField ?? "",
        tableAttributes?.sortAscending ?? true,
      );

      makePostCallForInitialDataFetch(
        payloads,
        authStatusOptions,
        setAllOpenOrdersState,
        setTableData,
        setInitialDataFetchingExecuted,
      );
    }
  }, [authStatusOptions, initialDataFetchingExecuted]);

  return {
    initialDataFetchingExecuted,
    setInitialDataFetchingExecuted,
  };
};
